import React, { useEffect, useState } from "react";
import GlobalStyle from "./components/styles/GlobalStyle";
import Grid from "./components/styles/Grid";
import Layout from "./components/Layout.js";
import { BrowserRouter as Router } from "react-router-dom";
import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "./authConfig";
import { MsalProvider } from "@azure/msal-react";
import { LoginRoutes, AppRoutes } from "./routes";
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { OrganizationContext, UserContext, ErrorContext } from "./contexts";
import { getOrganization } from "./api/services/organization";
import Error from "components/UI/Error";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

window.msalInstance = new PublicClientApplication(msalConfig);

const allesbonTheme = createTheme({
  palette: {
    primary:   { main: "#FF6B6B", dark: "#E8484F", light: "#FFA89A", contrastText: "#ffffff" },
    secondary: { main: "#3D1E5C", dark: "#1B0D2E", light: "#6B3FA0", contrastText: "#ffffff" },
    warning:   { main: "#F4C95D" },
    success:   { main: "#1bb775" },
    error:     { main: "#d62809" },
    background: { default: "#FFF8F0", paper: "#ffffff" },
    text:       { primary: "#1B0D2E", secondary: "#6B5B7A" },
  },
  shape: { borderRadius: 18 },
  typography: {
    fontFamily: "'Inter', 'Montserrat', sans-serif",
    h1: { fontFamily: "'Fraunces', serif", fontWeight: 700, letterSpacing: "-0.01em" },
    h2: { fontFamily: "'Fraunces', serif", fontWeight: 600, letterSpacing: "-0.01em" },
    h3: { fontFamily: "'Fraunces', serif", fontWeight: 600, letterSpacing: "-0.01em" },
    h4: { fontFamily: "'Fraunces', serif", fontWeight: 500 },
    h5: { fontFamily: "'Fraunces', serif", fontWeight: 500 },
    h6: { fontFamily: "'Fraunces', serif", fontWeight: 500 },
    button: { textTransform: "none", fontWeight: 600 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 999, paddingInline: 22, paddingBlock: 8, boxShadow: "none" },
        containedPrimary: {
          backgroundColor: "#FF6B6B", color: "#fff",
          boxShadow: "0 6px 18px rgba(232, 72, 79, 0.25)",
          "&:hover": { backgroundColor: "#E8484F", boxShadow: "0 10px 26px rgba(232, 72, 79, 0.32)" },
        },
        outlined: {
          borderColor: "rgba(61, 30, 92, 0.18)", color: "#3D1E5C",
          "&:hover": { borderColor: "#FF6B6B", color: "#FF6B6B", backgroundColor: "rgba(255, 107, 107, 0.04)" },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 18 },
        elevation1: { boxShadow: "0 6px 18px rgba(61, 30, 92, 0.08)" },
        elevation2: { boxShadow: "0 14px 40px rgba(61, 30, 92, 0.12)" },
      },
    },
    MuiAppBar: {
      styleOverrides: { root: { boxShadow: "0 18px 60px rgba(232, 72, 79, 0.18)" } },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: "transparent", borderRight: 0, backgroundImage: "linear-gradient(180deg, #FFF8F0 0%, #FCEDEA 100%)" },
      },
    },
    MuiBadge: {
      styleOverrides: { colorSecondary: { backgroundColor: "#FF6B6B", color: "#fff" } },
    },
    MuiSlider: {
      styleOverrides: { root: { color: "#FF6B6B" } },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          "&.Mui-checked": { color: "#FF6B6B" },
          "&:hover": { backgroundColor: "rgba(255, 107, 107, 0.08)" },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: { "&.Mui-checked": { color: "#FF6B6B" }, "&.Mui-checked + .MuiSwitch-track": { backgroundColor: "#FF6B6B" } },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: { "&.Mui-active": { color: "#FF6B6B" }, "&.Mui-completed": { color: "#F4C95D" } },
      },
    },
    MuiInput: {
      styleOverrides: {
        underline: {
          "&:after": { borderBottom: "2px solid #FF6B6B" },
          "&:hover:not(.Mui-disabled):before": { borderBottom: "1px solid #FFA89A" },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FF6B6B" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#FFA89A" },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: { root: { "&.Mui-focused": { color: "#FF6B6B" } } },
    },
    MuiTab: {
      styleOverrides: { root: { textTransform: "none", fontWeight: 600 } },
    },
    MuiTabs: {
      styleOverrides: { indicator: { backgroundColor: "#FF6B6B", height: 3, borderRadius: 999 } },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontWeight: 600 },
        colorPrimary: { backgroundColor: "#FCEDEA", color: "#E8484F" },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: "#3D1E5C", borderRadius: 8, fontSize: 12, padding: "6px 10px" },
        arrow: { color: "#3D1E5C" },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          "&:hover": { backgroundColor: "rgba(255, 107, 107, 0.08)" },
          "&.Mui-selected": { backgroundColor: "rgba(255, 107, 107, 0.12)", color: "#E8484F" },
        },
      },
    },
  },
});

export default function App() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const [userName, setUserName] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  const updateOrganizationData = async (id) => {
    if(id){
      const orgData = await getOrganization(id);
      setData({ ...orgData });
      setLoading(false);
    }
  };

  const updateUserData = async ( permissionData ) => {
    setPermissions(permissionData);    
  };

  const updateUserName = async ( userName ) => {
    setUserName(userName);    
  };

  const updateError = async ( error ) => {
    setErrorMessage(error);    
  };

  return (
    <MsalProvider instance={window.msalInstance}>
      <ThemeProvider theme={allesbonTheme}>
        <CssBaseline />
        <Router>
          <GlobalStyle />
          <Grid />
          <AuthenticatedTemplate>
            <OrganizationContext.Provider value={{ ...data, updateOrganizationData, loading }}>
              <UserContext.Provider value={{ permissions, updateUserData, userName, updateUserName }}>
                <ErrorContext.Provider value={{ errorMessage, updateError }}>
                  <Error/>
                  <Layout>
                    <AppRoutes />
                  </Layout>
                </ErrorContext.Provider>
              </UserContext.Provider>
            </OrganizationContext.Provider>
          </AuthenticatedTemplate>
          <UnauthenticatedTemplate>
            <LoginRoutes />
          </UnauthenticatedTemplate>
        </Router>
      </ThemeProvider>
    </MsalProvider>
  );
}
